import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import axios from 'axios';

interface BankReportProps {
  tableData: any[];
  pharmacyNum: string | null;
  startDate: string;
  endDate: string;
  selectedMonth?: string;
  employeeName: string;
  disabled?: boolean;
}

const BankReport: React.FC<BankReportProps> = ({
  tableData,
  pharmacyNum,
  startDate,
  endDate,
  selectedMonth,
  employeeName,
  disabled = false
}) => {

  const getMonthName = (): string => {
    const months = [
      'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
      'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
    ];
    if (selectedMonth) {
      const monthIndex = parseInt(selectedMonth.split('-')[1]) - 1;
      return months[monthIndex];
    }
    return '';
  };

  const getYear = (): string => {
    if (selectedMonth) {
      return selectedMonth.split('-')[0];
    }
    return '';
  };

  const [hasReports, setHasReports] = useState<boolean>(true);
  const [hasPos, setHasPos] = useState<boolean>(true);

  useEffect(() => {
    const fetchPharmacyNum = async () => {
      const username = localStorage.getItem('username');
      if (username) {
        try {
          const response = await axios.get('https://tbl.zerdegroup.kz/api/user-info', {
            params: { username }
          });
          if (response.data.success) {
            setHasReports(response.data.hasReports);
            setHasPos(response.data.pos === 1);
          }
        } catch (err) {
          console.error('Ошибка при получении номера аптеки:', err);
        }
      }
    };
    fetchPharmacyNum();
  }, []);

  const generateReport = async () => {
    try {
      const response = await fetch('/templates/bank_report_template.docx');
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();

      const getValue = (field: any) => {
        return field !== undefined && field !== null && field !== '' ? parseFloat(field) : 0;
      };

      const rows = tableData.map((row, index) => {
        const rowTotal =
          getValue(row.narodny_bank) +
          getValue(row.bereke_bank) +
          getValue(row.kaspi_red_terminal) +
          getValue(row.delivery_service_kaspi_store) +
          getValue(row.glovo) +
          getValue(row.volt) +
          getValue(row.yandex);

        return {
          num: index + 1,
          date: row.date,
          narodny_bank: getValue(row.narodny_bank),
          bereke_bank: getValue(row.bereke_bank),
          kaspi_red_terminal: getValue(row.kaspi_red_terminal),
          delivery_service_kaspi_store: getValue(row.delivery_service_kaspi_store),
          glovo: getValue(row.glovo),
          volt: getValue(row.volt),
          yandex: getValue(row.yandex),
          totalrow: rowTotal,
          cashless: getValue(row.cashless) || 0,
          total_revenue_zreport: getValue(row.total_revenue_zreport),
        };
      });

      const totals = rows.reduce((acc, row) => {
        const currentTotal =
          row.narodny_bank +
          row.bereke_bank +
          row.kaspi_red_terminal +
          row.delivery_service_kaspi_store +
          row.glovo +
          row.volt +
          row.yandex;

        return {
          totalnarodny_bank: acc.totalnarodny_bank + row.narodny_bank,
          totalBereke_bank: acc.totalBereke_bank + row.bereke_bank,
          totalkaspi_red_terminal: acc.totalkaspi_red_terminal + row.kaspi_red_terminal,
          totaldelivery_service_kaspi_store: acc.totaldelivery_service_kaspi_store + row.delivery_service_kaspi_store,
          totalglovo: acc.totalglovo + row.glovo,
          totalvolt: acc.totalvolt + row.volt,
          totalyandex: acc.totalyandex + row.yandex,
          totalrow: acc.totalrow + currentTotal,
          totalzreport: acc.totalzreport + row.cashless,
          totalTotal: acc.totalTotal + currentTotal,
          totalDifference: acc.totalzreport + row.cashless - (acc.totalTotal + currentTotal)
        };
      }, {
        totalnarodny_bank: 0,
        totalBereke_bank: 0,
        totalkaspi_red_terminal: 0,
        totaldelivery_service_kaspi_store: 0,
        totalglovo: 0,
        totalvolt: 0,
        totalyandex: 0,
        totalrow: 0,
        totalzreport: 0,
        totalTotal: 0,
        totalDifference: 0,
      });

      const data = {
        pharmacyNum: pharmacyNum ? pharmacyNum.toString() : '',
        currentMonth: getMonthName(),
        currentYear: getYear(),
        period: `${startDate} - ${endDate}`,
        employeeName: employeeName,
        rows,
        ...totals
      };

      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true
      });

      doc.render(data);

      const out = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const fileName = `Отчет о поступлении денег по реализации в кредит за ${selectedMonth || 'месяц'}.docx`;
      saveAs(out, fileName);

    } catch (error) {
      console.error('Ошибка при генерации отчета:', error);
    }
  };

  return (
    <>
      <Button
        color='secondary'
        variant="contained"
        onClick={generateReport}
        disabled={!tableData.length || !hasReports || !hasPos || !employeeName || disabled}
      >
        Банк
      </Button>
    </>
  );
};

export default BankReport;